// @ts-nocheck
/**
 * ツインキー状態異常エンジン
 *
 * 仕様書: twin_key_status_skill_system_v1.md
 * 設計書: twin_key_phase2_design.md
 */

import {
  STATUS_EFFECT_TYPES,
  STATUS_EFFECT_META,
  TIER_RESISTANCE,
  ATTRIBUTE_RESISTANCE,
  STACK_EFFECTS,
  getStatusMeta,
  getStatusName,
  getStatusIcon,
  getStackEffect,
  isIncapacitated,
  isSilenced,
} from './statusTypes';
import { ATTRIBUTE_ADVANTAGE } from '../../data/constants';

/**
 * 状態異常エンジンクラス
 *
 * 責務:
 * - 状態異常の付与成功率計算
 * - 状態異常の付与・解除
 * - ターン経過処理（DoT、持続時間減少）
 * - 状態異常による効果計算（ダメージ補正等）
 */
class StatusEffectEngine {
  constructor() {
    // 状態異常エンジンはステートレス設計
    // モンスターインスタンス側で状態異常を保持
  }

  /**
   * 付与成功率を計算
   *
   * 計算式:
   * 付与成功率 = 基礎成功率 + 不利補正 - 同属性耐性 - 有利属性耐性 - ティア耐性
   * ※最低20%、最大100%
   *
   * @param {string} statusType - 状態異常タイプ
   * @param {Object} target - 対象モンスター
   * @param {Object} source - 付与元モンスター（オプション）
   * @returns {number} 成功率 (0.20 ~ 1.00)
   */
  calculateSuccessRate(statusType, target, source = null) {
    const meta = getStatusMeta(statusType);
    if (!meta) return 0;

    let rate = meta.baseSuccessRate;

    // 魔障壁チェック（状態異常完全無効）
    if (target.hasBarrier) {
      return 0;
    }

    // 属性耐性計算
    if (meta.attribute && target.attribute) {
      // 同属性耐性
      if (meta.attribute === target.attribute) {
        rate -= ATTRIBUTE_RESISTANCE.same;
      }

      // 有利/不利属性耐性
      if (source?.attribute) {
        const advantage = this.getAttributeAdvantage(source.attribute, target.attribute);
        if (advantage === 'advantage') {
          // 攻撃側有利 = 対象は不利 → かかりやすい
          rate -= ATTRIBUTE_RESISTANCE.disadvantage; // +15%
        } else if (advantage === 'disadvantage') {
          // 攻撃側不利 = 対象は有利 → かかりにくい
          rate -= ATTRIBUTE_RESISTANCE.advantage; // -15%
        }
      }
    }

    // ティア耐性
    if (target.tier && TIER_RESISTANCE[target.tier] !== undefined) {
      rate -= TIER_RESISTANCE[target.tier];
    }

    // 範囲制限（最低20%、最大100%）
    return Math.max(0.20, Math.min(1.00, rate));
  }

  /**
   * 属性相性を取得
   * @private
   */
  getAttributeAdvantage(attackerAttr, defenderAttr) {
    if (!ATTRIBUTE_ADVANTAGE) return 'neutral';

    const advantageMap = ATTRIBUTE_ADVANTAGE[attackerAttr];
    if (!advantageMap) return 'neutral';

    if (advantageMap.strong === defenderAttr) return 'advantage';
    if (advantageMap.weak === defenderAttr) return 'disadvantage';
    return 'neutral';
  }

  /**
   * 状態異常を付与
   *
   * @param {Object} monster - 対象モンスター
   * @param {string} statusType - 状態異常タイプ
   * @param {Object} options - オプション
   * @param {number} options.duration - 持続ターン（省略時はデフォルト値）
   * @param {number} options.stack - 付与スタック数（省略時は1）
   * @param {Object} options.source - 付与元モンスター
   * @param {boolean} options.skipCheck - 成功率チェックをスキップ
   * @returns {{ success: boolean, monster: Object, message: string }}
   */
  applyStatus(monster, statusType, options = {}) {
    const meta = getStatusMeta(statusType);
    if (!meta) {
      return { success: false, monster, message: '無効な状態異常タイプ' };
    }

    const { duration, stack = 1, source = null, skipCheck = false } = options;

    // 成功率チェック
    if (!skipCheck) {
      const successRate = this.calculateSuccessRate(statusType, monster, source);
      if (Math.random() > successRate) {
        return {
          success: false,
          monster,
          message: `${monster.name}は${meta.name}を回避した！`,
        };
      }
    }

    // 状態異常配列を初期化
    const statusEffects = [...(monster.statusEffects || [])];

    // 既存の同タイプ状態異常を検索
    const existingIndex = statusEffects.findIndex((s) => s.type === statusType);

    if (existingIndex >= 0) {
      // 既存状態異常がある場合
      const existing = statusEffects[existingIndex];

      if (meta.stackable) {
        // スタック可能: スタック数を増加
        const newStack = Math.min(existing.stack + stack, meta.maxStack);
        statusEffects[existingIndex] = {
          ...existing,
          stack: newStack,
          // 持続時間は上書き（リフレッシュ）
          remainingTurns: duration ?? meta.defaultDuration,
        };
        return {
          success: true,
          monster: { ...monster, statusEffects },
          message: `${monster.name}の${meta.name}が${newStack}スタックになった！`,
        };
      } else {
        // スタック不可: 持続時間のみリフレッシュ
        statusEffects[existingIndex] = {
          ...existing,
          remainingTurns: duration ?? meta.defaultDuration,
        };
        return {
          success: true,
          monster: { ...monster, statusEffects },
          message: `${monster.name}の${meta.name}が延長された！`,
        };
      }
    } else {
      // 新規付与
      statusEffects.push({
        type: statusType,
        stack: Math.min(stack, meta.maxStack),
        remainingTurns: duration ?? meta.defaultDuration,
        source: source?.id || null,
      });
      return {
        success: true,
        monster: { ...monster, statusEffects },
        message: `${monster.name}は${meta.name}状態になった！`,
      };
    }
  }

  /**
   * 状態異常を解除
   *
   * @param {Object} monster - 対象モンスター
   * @param {string} statusType - 解除する状態異常タイプ
   * @returns {{ monster: Object, removed: boolean, message: string }}
   */
  removeStatus(monster, statusType) {
    const statusEffects = monster.statusEffects || [];
    const existingIndex = statusEffects.findIndex((s) => s.type === statusType);

    if (existingIndex < 0) {
      return { monster, removed: false, message: '' };
    }

    const meta = getStatusMeta(statusType);
    const newStatusEffects = statusEffects.filter((s) => s.type !== statusType);

    return {
      monster: { ...monster, statusEffects: newStatusEffects },
      removed: true,
      message: `${monster.name}の${meta?.name || statusType}が解除された！`,
    };
  }

  /**
   * 全状態異常を解除（戦闘終了時など）
   */
  clearAllStatus(monster) {
    return {
      ...monster,
      statusEffects: [],
    };
  }

  /**
   * ターン開始時処理
   * - 麻痺の行動失敗判定は別途（行動時に判定）
   *
   * @param {Object} monster - モンスター
   * @returns {{ monster: Object, messages: string[] }}
   */
  processTurnStart(monster) {
    // 現状ターン開始時に特別な処理はなし
    // （持続時間減少はターン終了時）
    return { monster, messages: [] };
  }

  /**
   * ターン終了時処理
   * - DoTダメージ（火傷、毒）
   * - 持続時間減少
   * - 自然解除
   *
   * @param {Object} monster - モンスター
   * @returns {{ monster: Object, damage: number, messages: string[], removed: string[] }}
   */
  processTurnEnd(monster) {
    if (!monster.statusEffects || monster.statusEffects.length === 0) {
      return { monster, damage: 0, messages: [], removed: [] };
    }

    let totalDamage = 0;
    const messages = [];
    const removed = [];
    let statusEffects = [...monster.statusEffects];
    let currentHp = monster.currentHp;
    const maxHp = monster.maxHp || monster.hp;

    statusEffects = statusEffects.map((status) => {
      const meta = getStatusMeta(status.type);
      if (!meta) return status;

      // DoTダメージ処理
      if (status.type === STATUS_EFFECT_TYPES.BURN) {
        const effect = getStackEffect(status.type, status.stack);
        const damage = Math.floor(maxHp * effect.hpPercent);
        totalDamage += damage;
        currentHp = Math.max(0, currentHp - damage);
        messages.push(`${monster.name}は火傷で${damage}ダメージを受けた！`);
      }

      if (status.type === STATUS_EFFECT_TYPES.POISON) {
        const effect = getStackEffect(status.type, status.stack);
        const damage = Math.floor(maxHp * effect.hpPercent);
        totalDamage += damage;
        currentHp = Math.max(0, currentHp - damage);
        messages.push(`${monster.name}は毒で${damage}ダメージを受けた！`);
      }

      // 持続時間減少（永続は除く）
      if (status.remainingTurns > 0) {
        return { ...status, remainingTurns: status.remainingTurns - 1 };
      }
      return status;
    });

    // 持続時間切れの状態異常を解除
    statusEffects = statusEffects.filter((status) => {
      const meta = getStatusMeta(status.type);
      // 永続（-1）または残りターン > 0 なら維持
      if (status.remainingTurns < 0 || status.remainingTurns > 0) {
        return true;
      }
      // 自然解除
      if (meta?.removeCondition === 'natural') {
        removed.push(status.type);
        messages.push(`${monster.name}の${meta.name}が解除された！`);
        return false;
      }
      return true;
    });

    return {
      monster: {
        ...monster,
        currentHp,
        statusEffects,
      },
      damage: totalDamage,
      messages,
      removed,
    };
  }

  /**
   * ダメージ受けた時の処理（凍結解除など）
   *
   * @param {Object} monster - ダメージを受けたモンスター
   * @returns {{ monster: Object, messages: string[] }}
   */
  onDamageReceived(monster) {
    if (!monster.statusEffects) return { monster, messages: [] };

    const messages = [];
    let statusEffects = [...monster.statusEffects];

    // 凍結はダメージで解除
    const freezeIndex = statusEffects.findIndex(
      (s) => s.type === STATUS_EFFECT_TYPES.FREEZE
    );
    if (freezeIndex >= 0) {
      statusEffects = statusEffects.filter(
        (s) => s.type !== STATUS_EFFECT_TYPES.FREEZE
      );
      messages.push(`${monster.name}の凍結が解除された！`);
    }

    return {
      monster: { ...monster, statusEffects },
      messages,
    };
  }

  /**
   * 回復時の処理（毒解除など）
   *
   * @param {Object} monster - 回復したモンスター
   * @returns {{ monster: Object, messages: string[] }}
   */
  onHealReceived(monster) {
    if (!monster.statusEffects) return { monster, messages: [] };

    const messages = [];
    let statusEffects = [...monster.statusEffects];

    // 毒は回復で解除
    const poisonIndex = statusEffects.findIndex(
      (s) => s.type === STATUS_EFFECT_TYPES.POISON
    );
    if (poisonIndex >= 0) {
      statusEffects = statusEffects.filter(
        (s) => s.type !== STATUS_EFFECT_TYPES.POISON
      );
      messages.push(`${monster.name}の毒が解除された！`);
    }

    return {
      monster: { ...monster, statusEffects },
      messages,
    };
  }

  // ==================== 効果計算メソッド ====================

  /**
   * 行動可能かどうか判定
   *
   * @param {Object} monster - モンスター
   * @returns {{ canAct: boolean, reason: string | null }}
   */
  canAct(monster) {
    if (!monster.statusEffects) return { canAct: true, reason: null };

    // 凍結・スタンチェック
    for (const status of monster.statusEffects) {
      if (isIncapacitated(status.type)) {
        const meta = getStatusMeta(status.type);
        return { canAct: false, reason: `${meta?.name || status.type}状態のため行動不能` };
      }
    }

    // 麻痺チェック（50%で行動失敗）
    const hasParalysis = monster.statusEffects.some(
      (s) => s.type === STATUS_EFFECT_TYPES.PARALYSIS
    );
    if (hasParalysis && Math.random() < 0.5) {
      return { canAct: false, reason: '麻痺で体がしびれて動けない！' };
    }

    return { canAct: true, reason: null };
  }

  /**
   * スキル使用可能かどうか判定
   *
   * @param {Object} monster - モンスター
   * @returns {{ canUseSkill: boolean, reason: string | null }}
   */
  canUseSkill(monster) {
    if (!monster.statusEffects) return { canUseSkill: true, reason: null };

    for (const status of monster.statusEffects) {
      if (isSilenced(status.type)) {
        return { canUseSkill: false, reason: '沈黙状態のためスキル使用不可' };
      }
    }

    return { canUseSkill: true, reason: null };
  }

  /**
   * 攻撃力補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 攻撃力倍率（1.0 = 100%）
   */
  getAttackMultiplier(monster) {
    if (!monster.statusEffects) return 1.0;

    let multiplier = 1.0;

    for (const status of monster.statusEffects) {
      if (status.type === STATUS_EFFECT_TYPES.ATK_DOWN) {
        const effect = getStackEffect(status.type, status.stack);
        multiplier *= effect.atkMultiplier;
      }
    }

    return multiplier;
  }

  /**
   * 与ダメージ補正を計算（虚脱）
   *
   * @param {Object} monster - モンスター
   * @returns {number} 与ダメージ倍率（1.0 = 100%）
   */
  getDamageDealtMultiplier(monster) {
    if (!monster.statusEffects) return 1.0;

    let multiplier = 1.0;

    for (const status of monster.statusEffects) {
      if (status.type === STATUS_EFFECT_TYPES.EXHAUST) {
        const effect = getStackEffect(status.type, status.stack);
        multiplier *= effect.damageMultiplier;
      }
    }

    return multiplier;
  }

  /**
   * 被ダメージ補正を計算（凍結）
   *
   * @param {Object} monster - モンスター
   * @returns {number} 被ダメージ倍率（1.0 = 100%）
   */
  getDamageReceivedMultiplier(monster) {
    if (!monster.statusEffects) return 1.0;

    let multiplier = 1.0;

    // 凍結: 被ダメージ+25%
    const hasFrozen = monster.statusEffects.some(
      (s) => s.type === STATUS_EFFECT_TYPES.FREEZE
    );
    if (hasFrozen) {
      multiplier *= 1.25;
    }

    return multiplier;
  }

  /**
   * 防御補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 防御減少値（加算）
   */
  getDefenseModifier(monster) {
    if (!monster.statusEffects) return 0;

    let modifier = 0;

    for (const status of monster.statusEffects) {
      if (status.type === STATUS_EFFECT_TYPES.DEF_DOWN) {
        const effect = getStackEffect(status.type, status.stack);
        modifier += effect.def;
      }
    }

    return modifier;
  }

  /**
   * 速度補正を計算
   *
   * @param {Object} monster - モンスター
   * @returns {number} 速度減少値（加算）
   */
  getSpeedModifier(monster) {
    if (!monster.statusEffects) return 0;

    let modifier = 0;

    for (const status of monster.statusEffects) {
      if (status.type === STATUS_EFFECT_TYPES.TIME_STOP) {
        const effect = getStackEffect(status.type, status.stack);
        modifier += effect.speed;
      }
      if (status.type === STATUS_EFFECT_TYPES.SPD_DOWN) {
        const effect = getStackEffect(status.type, status.stack);
        modifier += effect.speed;
      }
    }

    return modifier;
  }

  /**
   * 回復効果補正を計算（呪い）
   *
   * @param {Object} monster - モンスター
   * @returns {number} 回復倍率（1.0 = 100%）
   */
  getHealMultiplier(monster) {
    if (!monster.statusEffects) return 1.0;

    // 呪い: 回復効果50%減
    const hasCurse = monster.statusEffects.some(
      (s) => s.type === STATUS_EFFECT_TYPES.CURSE
    );
    if (hasCurse) {
      return 0.5;
    }

    return 1.0;
  }

  /**
   * 特定の状態異常を持っているか確認
   *
   * @param {Object} monster - モンスター
   * @param {string} statusType - 状態異常タイプ
   * @returns {boolean}
   */
  hasStatus(monster, statusType) {
    if (!monster.statusEffects) return false;
    return monster.statusEffects.some((s) => s.type === statusType);
  }

  /**
   * 状態異常のスタック数を取得
   *
   * @param {Object} monster - モンスター
   * @param {string} statusType - 状態異常タイプ
   * @returns {number} スタック数（なければ0）
   */
  getStatusStack(monster, statusType) {
    if (!monster.statusEffects) return 0;
    const status = monster.statusEffects.find((s) => s.type === statusType);
    return status?.stack || 0;
  }

  /**
   * モンスターの状態異常サマリーを取得（UI表示用）
   *
   * @param {Object} monster - モンスター
   * @returns {Array<{ type: string, name: string, icon: string, stack: number, turns: number }>}
   */
  getStatusSummary(monster) {
    if (!monster.statusEffects) return [];

    return monster.statusEffects.map((status) => {
      const meta = getStatusMeta(status.type);
      return {
        type: status.type,
        name: meta?.name || status.type,
        icon: getStatusIcon(status.type),
        stack: status.stack,
        turns: status.remainingTurns,
      };
    });
  }
}

// シングルトンインスタンス
export const statusEffectEngine = new StatusEffectEngine();
export default StatusEffectEngine;
